/**
 * annot-core.js — рисование поверх захвата экрана: модель пометок и ОДИН
 * рисовальщик для всех мест, где пометка появляется.
 *
 * Одна и та же функция render() рисует пометки:
 *   · в превью на странице (человек видит то, что рисует);
 *   · в кадре записи /screen-recorder (фильтр кадров движка, annot-layer.js);
 *   · у зрителя /screen-share поверх его видео (векторы по каналу данных).
 * Поэтому все размеры здесь — доли кадра, а не пиксели: толщина — доля
 * КОРОТКОЙ стороны картинки, координаты — доли её ширины и высоты. Превью
 * 640 px и файл 2560 px получают одну и ту же линию в одном и том же месте.
 *
 * Чистый модуль: ни DOM, ни часов (время приходит параметром `now`), поэтому
 * гоняется в node (tests/js/screen-annotate-core.test.mjs).
 *
 * Пометка (item):
 *   id   — строка, уникальная в документе (у ведущего трансляции — «h12»);
 *   k    — вид: pen | hl (маркер) | laser | line | arrow | rect | oval | text | step | blur;
 *   c    — цвет CSS;
 *   w    — толщина, доля короткой стороны (S 0.004 · M 0.007 · L 0.012);
 *   p    — точки [x0,y0,x1,y1,…] в долях картинки; у фигур — два угла,
 *          у текста и номера — одна точка;
 *   t    — текст (text); n — номер (step);
 *   life — сколько пометка живёт после завершения, мс; 0 — пока не сотрут;
 *   done — момент завершения по часам того, кто рисует (null — ещё рисуется).
 * Указка (laser) живёт по модели tldraw: все её штрихи гаснут ВМЕСТЕ через
 * LASER_IDLE_MS после последнего движения указкой (замер их редизайна:
 * поштучное угасание рвёт подчёркивание на куски).
 *
 * Разбор — docs/why/primitives/screen-annotate.md.
 */
(function (root, factory) {
    var api = factory();
    if (typeof module === 'object' && module.exports) module.exports = api;
    else root.ScreenAnnotCore = api;
})(typeof window !== 'undefined' ? window : this, function () {
    'use strict';

    var FADE_MS = 400;          // последняя часть жизни угасающей пометки
    var LASER_IDLE_MS = 1200;   // указка гаснет через столько после последнего движения
    var LASER_FADE_MS = 350;
    var SIZES = { s: 0.004, m: 0.007, l: 0.012 };
    var KINDS = ['pen', 'hl', 'laser', 'line', 'arrow', 'rect', 'oval', 'text', 'step', 'blur'];
    var MAX_ITEMS = 1500;       // потолок документа: старейшие уходят первыми
    var MAX_POINTS = 4000;      // потолок точек одного штриха
    var REMOTE_MS = 1500;       // указка зрителя гаснет через столько после последнего движения
    var ZOOM_MS = 280;          // лупа въезжает и выезжает плавно, а не скачком
    var ZOOM_MIN = 0.08;        // глубже 12× не приближаем: дальше видно только пиксели

    function clamp(v, a, b) { return v < a ? a : v > b ? b : v; }
    function isKind(k) { return KINDS.indexOf(k) >= 0; }

    // ── жизнь пометки ─────────────────────────────────────────────────────
    // Непрозрачность 0…1 в момент `now`; 0 — пометка ушла (её можно убрать).
    function alphaOf(it, now, laserIdleAt) {
        if (it.k === 'laser') {
            if (!it.done && laserIdleAt === undefined) return 1;
            var last = laserIdleAt !== undefined ? laserIdleAt : (it.done || now);
            var left = last + LASER_IDLE_MS + LASER_FADE_MS - now;
            if (left >= LASER_FADE_MS) return 1;
            return left <= 0 ? 0 : left / LASER_FADE_MS;
        }
        if (!it.life || !it.done) return 1;
        var rest = it.done + it.life - now;
        if (rest >= FADE_MS) return 1;
        return rest <= 0 ? 0 : rest / FADE_MS;
    }

    // Убирает ушедшие пометки. Возвращает true, если что-то ещё угасает
    // (тогда кадр надо перерисовывать, даже если никто не рисует).
    function prune(items, now, laserIdleAt) {
        var animating = false;
        for (var i = items.length - 1; i >= 0; i--) {
            var a = alphaOf(items[i], now, laserIdleAt);
            if (a <= 0) { items.splice(i, 1); continue; }
            if (a < 1 || (items[i].k === 'laser') || (items[i].life && items[i].done)) animating = true;
        }
        return animating;
    }

    // ── геометрия ─────────────────────────────────────────────────────────
    function short(R) { return Math.min(R.w, R.h); }
    function X(R, v) { return R.x + v * R.w; }
    function Y(R, v) { return R.y + v * R.h; }

    // Гладкая линия через середины отрезков: перо не выглядит ломаной даже
    // при редких событиях мыши (60 Гц у большинства, 8 Гц у тачпада под нагрузкой).
    function smoothPath(ctx, R, p) {
        ctx.beginPath();
        var n = p.length / 2;
        ctx.moveTo(X(R, p[0]), Y(R, p[1]));
        if (n === 1) { ctx.lineTo(X(R, p[0]) + 0.01, Y(R, p[1]) + 0.01); return; }
        if (n === 2) { ctx.lineTo(X(R, p[2]), Y(R, p[3])); return; }
        for (var i = 1; i < n - 1; i++) {
            var cx = X(R, p[i * 2]), cy = Y(R, p[i * 2 + 1]);
            var mx = (cx + X(R, p[i * 2 + 2])) / 2, my = (cy + Y(R, p[i * 2 + 3])) / 2;
            ctx.quadraticCurveTo(cx, cy, mx, my);
        }
        ctx.lineTo(X(R, p[p.length - 2]), Y(R, p[p.length - 1]));
    }

    // Фигура по двум углам; `sq` — Shift: квадрат / круг / угол кратный 45°.
    function shapeBox(R, p) {
        var x0 = X(R, p[0]), y0 = Y(R, p[1]), x1 = X(R, p[2]), y1 = Y(R, p[3]);
        return { x: Math.min(x0, x1), y: Math.min(y0, y1), w: Math.abs(x1 - x0), h: Math.abs(y1 - y0), x0: x0, y0: y0, x1: x1, y1: y1 };
    }

    // Тень, по которой линия читается на любом фоне: белая — на белом
    // документе, чёрная — на тёмной теме. Свечение своим цветом (как было
    // до 04.11.2026) на белом экране делало жёлтое невидимым.
    function legible(ctx, s) {
        ctx.shadowColor = 'rgba(0,0,0,.42)';
        ctx.shadowBlur = Math.max(1, s * 0.004);
        ctx.shadowOffsetY = Math.max(0.5, s * 0.0012);
    }
    function plain(ctx) { ctx.shadowColor = 'transparent'; ctx.shadowBlur = 0; ctx.shadowOffsetY = 0; }

    function arrowHead(ctx, x0, y0, x1, y1, lw) {
        var a = Math.atan2(y1 - y0, x1 - x0);
        var len = Math.max(lw * 4.2, 10);
        var spread = 0.48;
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x1 - len * Math.cos(a - spread), y1 - len * Math.sin(a - spread));
        ctx.lineTo(x1 - len * Math.cos(a + spread), y1 - len * Math.sin(a + spread));
        ctx.closePath();
        ctx.fill();
    }
    // Древко стрелки кончается у основания наконечника — иначе толстая линия
    // торчит из-под острия тупым концом.
    function arrowShaftEnd(x0, y0, x1, y1, lw) {
        var d = Math.hypot(x1 - x0, y1 - y0) || 1;
        var back = Math.min(d * 0.6, Math.max(lw * 4.2, 10) * 0.75);
        return { x: x1 - (x1 - x0) / d * back, y: y1 - (y1 - y0) / d * back };
    }

    function textSize(it, s) { return clamp((it.w || SIZES.m) * 6.5, 0.018, 0.12) * s; }
    function stepRadius(it, s) { return clamp((it.w || SIZES.m) * 3.2, 0.014, 0.06) * s; }

    // ── мозаика («скрыть пароль») ─────────────────────────────────────────
    // Рисуется ПЕРВОЙ, по уже нарисованной картинке: участок холста
    // уменьшается в 1/MOSAIC и растягивается обратно без сглаживания. Не
    // `ctx.filter = 'blur()'`: Safari его не умеет, а размытие сильного текста
    // иногда читается — квадраты по 1.2 % стороны не читаются никогда.
    function paintBlur(ctx, R, it, opts) {
        if (!opts || !opts.scratch) return;
        var b = shapeBox(R, it.p);
        var bx = Math.max(R.x, Math.floor(b.x)), by = Math.max(R.y, Math.floor(b.y));
        var bw = Math.min(R.x + R.w, Math.ceil(b.x + b.w)) - bx, bh = Math.min(R.y + R.h, Math.ceil(b.y + b.h)) - by;
        if (bw < 2 || bh < 2) return;
        var cell = Math.max(4, Math.round(short(R) * 0.012));
        var sw = Math.max(1, Math.round(bw / cell)), sh = Math.max(1, Math.round(bh / cell));
        var tmp = opts.scratch(sw, sh);
        if (!tmp) return;
        var tctx = tmp.getContext('2d');
        try {
            tctx.imageSmoothingEnabled = true;
            tctx.clearRect(0, 0, sw, sh);
            tctx.drawImage(ctx.canvas, bx, by, bw, bh, 0, 0, sw, sh);
            ctx.save();
            ctx.imageSmoothingEnabled = false;
            ctx.drawImage(tmp, 0, 0, sw, sh, bx, by, bw, bh);
            ctx.restore();
        } catch (_) {}
    }

    // ── одна пометка ──────────────────────────────────────────────────────
    function paintItem(ctx, R, it, alpha, opts) {
        var s = short(R);
        var lw = Math.max(1, (it.w || SIZES.m) * s);
        var p = it.p || [];
        if (!p.length) return;
        ctx.save();
        ctx.globalAlpha = alpha;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.strokeStyle = it.c;
        ctx.fillStyle = it.c;
        switch (it.k) {
            case 'pen':
                legible(ctx, s);
                ctx.lineWidth = lw;
                smoothPath(ctx, R, p);
                ctx.stroke();
                break;
            case 'hl':
                // Маркер — полупрозрачная широкая полоса. Один stroke() на весь
                // путь, поэтому самопересечения не темнеют.
                ctx.globalAlpha = alpha * 0.36;
                ctx.lineCap = 'butt';
                ctx.lineWidth = lw * 3.4;
                smoothPath(ctx, R, p);
                ctx.stroke();
                break;
            case 'laser':
                // Светящаяся линия: широкое цветное свечение + белая сердцевина.
                ctx.shadowColor = it.c;
                ctx.shadowBlur = lw * 3;
                ctx.lineWidth = lw * 1.25;
                smoothPath(ctx, R, p);
                ctx.stroke();
                plain(ctx);
                ctx.strokeStyle = 'rgba(255,255,255,.9)';
                ctx.lineWidth = Math.max(1, lw * 0.4);
                smoothPath(ctx, R, p);
                ctx.stroke();
                break;
            case 'line':
            case 'arrow':
                if (p.length < 4) break;
                legible(ctx, s);
                ctx.lineWidth = lw;
                var x0 = X(R, p[0]), y0 = Y(R, p[1]), x1 = X(R, p[2]), y1 = Y(R, p[3]);
                var end = it.k === 'arrow' ? arrowShaftEnd(x0, y0, x1, y1, lw) : { x: x1, y: y1 };
                ctx.beginPath(); ctx.moveTo(x0, y0); ctx.lineTo(end.x, end.y); ctx.stroke();
                if (it.k === 'arrow') arrowHead(ctx, x0, y0, x1, y1, lw);
                break;
            case 'rect':
                if (p.length < 4) break;
                legible(ctx, s);
                ctx.lineWidth = lw;
                var b = shapeBox(R, p);
                ctx.beginPath();
                var rr = Math.min(lw * 1.5, b.w / 2, b.h / 2);
                if (ctx.roundRect) ctx.roundRect(b.x, b.y, b.w, b.h, rr); else ctx.rect(b.x, b.y, b.w, b.h);
                ctx.stroke();
                break;
            case 'oval':
                if (p.length < 4) break;
                legible(ctx, s);
                ctx.lineWidth = lw;
                var o = shapeBox(R, p);
                ctx.beginPath();
                ctx.ellipse(o.x + o.w / 2, o.y + o.h / 2, Math.max(0.5, o.w / 2), Math.max(0.5, o.h / 2), 0, 0, Math.PI * 2);
                ctx.stroke();
                break;
            case 'text':
                if (!it.t) break;
                var fs = textSize(it, s);
                ctx.font = '700 ' + Math.round(fs) + 'px system-ui,-apple-system,"Segoe UI",Roboto,Arial,sans-serif';
                ctx.textBaseline = 'top';
                // Обводка — тёмная, чтобы светлый текст читался на светлом.
                ctx.lineWidth = Math.max(2, fs * 0.16);
                ctx.strokeStyle = isDark(it.c) ? 'rgba(255,255,255,.85)' : 'rgba(0,0,0,.72)';
                var lines = String(it.t).split('\n');
                for (var li = 0; li < lines.length; li++) {
                    var ty = Y(R, p[1]) + li * fs * 1.18;
                    ctx.strokeText(lines[li], X(R, p[0]), ty);
                    ctx.fillText(lines[li], X(R, p[0]), ty);
                }
                break;
            case 'step':
                var r = stepRadius(it, s);
                var cx = X(R, p[0]), cy = Y(R, p[1]);
                legible(ctx, s);
                ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.fill();
                plain(ctx);
                ctx.lineWidth = Math.max(1.5, r * 0.12);
                ctx.strokeStyle = 'rgba(255,255,255,.95)';
                ctx.stroke();
                ctx.fillStyle = isDark(it.c) ? '#fff' : '#111';
                ctx.font = '800 ' + Math.round(r * (String(it.n).length > 1 ? 1.0 : 1.25)) + 'px system-ui,-apple-system,"Segoe UI",Roboto,Arial,sans-serif';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillText(String(it.n || 1), cx, cy + r * 0.05);
                break;
        }
        ctx.restore();
    }

    function isDark(c) {
        var m = /^#([0-9a-f]{6})$/i.exec(c || '');
        if (!m) return false;
        var v = parseInt(m[1], 16), r = (v >> 16) & 255, g = (v >> 8) & 255, b = v & 255;
        return (0.299 * r + 0.587 * g + 0.114 * b) < 110;
    }

    // ── указатель: точка указки и прожектор ───────────────────────────────
    // ptr = {x, y, k:'laser'|'spot', c, r} в долях картинки; null — нет.
    function paintPointer(ctx, R, ptr) {
        if (!ptr || ptr.x === undefined) return;
        var s = short(R), px = X(R, ptr.x), py = Y(R, ptr.y);
        ctx.save();
        if (ptr.k === 'spot') {
            // Прожектор: всё, кроме круга, притемнено; край мягкий.
            var r = clamp(ptr.r || 0.16, 0.05, 0.5) * s;
            var g = ctx.createRadialGradient(px, py, r * 0.86, px, py, r * 1.04);
            g.addColorStop(0, 'rgba(0,0,0,0)');
            g.addColorStop(1, 'rgba(0,0,0,.62)');
            ctx.fillStyle = g;
            ctx.fillRect(R.x, R.y, R.w, R.h);
        } else {
            var d = Math.max(4, s * 0.009);
            ctx.shadowColor = ptr.c || '#ff2d55';
            ctx.shadowBlur = d * 2.6;
            ctx.fillStyle = ptr.c || '#ff2d55';
            ctx.beginPath(); ctx.arc(px, py, d, 0, Math.PI * 2); ctx.fill();
            ctx.shadowBlur = 0;
            ctx.fillStyle = 'rgba(255,255,255,.9)';
            ctx.beginPath(); ctx.arc(px, py, d * 0.42, 0, Math.PI * 2); ctx.fill();
        }
        ctx.restore();
    }

    // ── указки зрителей (/screen-share): точка цвета зрителя и его имя ────
    // list = [{x, y, c, n, t}] — n подпись («Зритель 2»), t — время последнего
    // движения по часам ЭТОЙ машины: указка гаснет за REMOTE_MS сама.
    function paintRemote(ctx, R, list, now) {
        if (!list || !list.length) return;
        var s = short(R);
        for (var i = 0; i < list.length; i++) {
            var p = list[i];
            var age = now - (p.t || now);
            if (age > REMOTE_MS) continue;
            var a = age < REMOTE_MS - 300 ? 1 : (REMOTE_MS - age) / 300;
            var px = X(R, p.x), py = Y(R, p.y), d = Math.max(4, s * 0.008);
            ctx.save();
            ctx.globalAlpha = a;
            ctx.shadowColor = p.c || '#0a84ff';
            ctx.shadowBlur = d * 2.2;
            ctx.fillStyle = p.c || '#0a84ff';
            ctx.beginPath(); ctx.arc(px, py, d, 0, Math.PI * 2); ctx.fill();
            ctx.shadowBlur = 0;
            if (p.n) {
                var fs = Math.max(11, Math.round(s * 0.022));
                ctx.font = '600 ' + fs + 'px system-ui,-apple-system,"Segoe UI",Roboto,Arial,sans-serif';
                var tw = ctx.measureText(p.n).width;
                var bx = px + d * 1.6, by = py + d * 1.2;
                ctx.fillStyle = p.c || '#0a84ff';
                ctx.beginPath();
                if (ctx.roundRect) ctx.roundRect(bx, by, tw + fs * 0.9, fs * 1.5, fs * 0.4); else ctx.rect(bx, by, tw + fs * 0.9, fs * 1.5);
                ctx.fill();
                ctx.fillStyle = isDark(p.c) ? '#fff' : '#111';
                ctx.textBaseline = 'middle';
                ctx.fillText(p.n, bx + fs * 0.45, by + fs * 0.76);
            }
            ctx.restore();
        }
    }

    // ── весь слой ─────────────────────────────────────────────────────────
    // ctx — холст, на котором картинка уже нарисована в прямоугольник R
    // (в превью — вписанный; в кадре записи — весь кадр).
    // opts: { laserIdleAt, ptr, scratch(w,h) → canvas (для мозаики) }
    function render(ctx, R, items, now, opts) {
        opts = opts || {};
        if (!R || R.w <= 0 || R.h <= 0) return;
        // Мозаика первой: она закрывает картинку, а не пометки поверх неё.
        for (var i = 0; i < items.length; i++) {
            if (items[i].k !== 'blur') continue;
            var ab = alphaOf(items[i], now, opts.laserIdleAt);
            if (ab > 0) paintBlur(ctx, R, items[i], opts);
        }
        if (opts.ptr && opts.ptr.k === 'spot') paintPointer(ctx, R, opts.ptr);
        for (var j = 0; j < items.length; j++) {
            var it = items[j];
            if (it.k === 'blur') continue;
            var a = alphaOf(it, now, opts.laserIdleAt);
            if (a > 0) paintItem(ctx, R, it, a, opts);
        }
        if (opts.ptr && opts.ptr.k !== 'spot') paintPointer(ctx, R, opts.ptr);
        if (opts.remote) paintRemote(ctx, R, opts.remote, now);
    }

    // Рамка невидимой мозаики, пока её тянут: в превью человек должен видеть,
    // ЧТО закроется, а в кадр рамка не идёт.
    function renderGuides(ctx, R, items) {
        for (var i = 0; i < items.length; i++) {
            var it = items[i];
            if (it.k !== 'blur' || !it.p || it.p.length < 4) continue;
            var b = shapeBox(R, it.p);
            ctx.save();
            ctx.setLineDash([6, 5]);
            ctx.lineWidth = 1.5;
            ctx.strokeStyle = 'rgba(255,255,255,.8)';
            ctx.strokeRect(b.x + 0.5, b.y + 0.5, b.w, b.h);
            ctx.restore();
        }
    }

    // ── лупа: участок основы, который встаёт на весь кадр ─────────────────
    // Состояние z = {from, to, at}: from/to — прямоугольники в долях основы
    // (null — весь кадр), at — начало перехода. Доли ширины и высоты у участка
    // РАВНЫ (w === h): тогда в пикселях он той же формы, что кадр, и
    // приближение не растягивает картинку. Пометки, нарисованные на лупе,
    // живут в координатах ВИДА (того, что на экране), поэтому смена лупы
    // гасит их, как смена слайда.
    var FULL = { x: 0, y: 0, w: 1, h: 1 };
    function cleanRect(r) {
        if (!r) return null;
        var w = clamp(+r.w || 0, ZOOM_MIN, 1);
        var x = clamp(+r.x || 0, 0, 1 - w), y = clamp(+r.y || 0, 0, 1 - w);
        if (w >= 0.999) return null;
        return { x: x, y: y, w: w, h: w };
    }
    function ease(t) { return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2; }
    // Прямоугольник лупы в момент now (null — лупы нет).
    function zoomNow(z, now) {
        if (!z) return null;
        var t = clamp((now - z.at) / ZOOM_MS, 0, 1);
        if (t >= 1) return z.to;
        var a = z.from || FULL, b = z.to || FULL, e = ease(t);
        return { x: a.x + (b.x - a.x) * e, y: a.y + (b.y - a.y) * e, w: a.w + (b.w - a.w) * e, h: a.h + (b.h - a.h) * e };
    }
    function zoomBusy(z, now) { return !!z && now - z.at < ZOOM_MS; }
    function zoomTo(z, rect, now) { return { from: zoomNow(z, now), to: cleanRect(rect), at: now }; }
    // Участок, обведённый на ВИДЕ (доли того, что на экране), — в доли основы.
    function zoomCompose(cur, v) {
        cur = cur || FULL;
        return cleanRect({ x: cur.x + v.x * cur.w, y: cur.y + v.y * cur.h, w: v.w * cur.w, h: v.h * cur.h });
    }
    // Нарисовать источник с лупой в прямоугольник назначения.
    function drawZoomed(ctx, src, sw, sh, z, dx, dy, dw, dh) {
        if (z) ctx.drawImage(src, z.x * sw, z.y * sh, z.w * sw, z.h * sh, dx, dy, dw, dh);
        else ctx.drawImage(src, dx, dy, dw, dh);
    }

    // ── вписать картинку в коробку (object-fit: contain) ──────────────────
    function fitRect(boxW, boxH, picW, picH) {
        if (!picW || !picH || !boxW || !boxH) return { x: 0, y: 0, w: boxW, h: boxH };
        var sc = Math.min(boxW / picW, boxH / picH);
        var w = picW * sc, h = picH * sc;
        return { x: (boxW - w) / 2, y: (boxH - h) / 2, w: w, h: h };
    }
    // Точка указателя (px коробки) → доли картинки; null — мимо картинки.
    function toNorm(R, px, py, slack) {
        var x = (px - R.x) / R.w, y = (py - R.y) / R.h;
        var e = slack || 0;
        if (x < -e || y < -e || x > 1 + e || y > 1 + e) return null;
        return { x: clamp(x, 0, 1), y: clamp(y, 0, 1) };
    }

    // ── ластик: попадание в пометку ───────────────────────────────────────
    // (x, y) — доли; aspect = ширина/высота картинки (доли по x и y не
    // равны по длине, расстояния считаются в «долях короткой стороны»).
    function distSeg(px, py, ax, ay, bx, by) {
        var dx = bx - ax, dy = by - ay, l2 = dx * dx + dy * dy;
        var t = l2 ? clamp(((px - ax) * dx + (py - ay) * dy) / l2, 0, 1) : 0;
        var cx = ax + t * dx, cy = ay + t * dy;
        return Math.hypot(px - cx, py - cy);
    }
    function hitTest(items, x, y, aspect, tol) {
        aspect = aspect || 16 / 9;
        // перевод долей в единицы короткой стороны
        var kx = aspect >= 1 ? aspect : 1, ky = aspect >= 1 ? 1 : 1 / aspect;
        var px = x * kx, py = y * ky;
        tol = tol || 0.012;
        for (var i = items.length - 1; i >= 0; i--) {
            var it = items[i], p = it.p || [], w = (it.w || SIZES.m) / 2;
            if (it.k === 'laser') continue;
            var hit = false;
            if (it.k === 'pen' || it.k === 'hl') {
                var ww = it.k === 'hl' ? w * 3.4 : w;
                if (p.length === 2) hit = Math.hypot(p[0] * kx - px, p[1] * ky - py) <= tol + ww;
                for (var j = 0; !hit && j + 3 < p.length; j += 2) {
                    if (distSeg(px, py, p[j] * kx, p[j + 1] * ky, p[j + 2] * kx, p[j + 3] * ky) <= tol + ww) hit = true;
                }
            } else if (it.k === 'line' || it.k === 'arrow') {
                hit = p.length >= 4 && distSeg(px, py, p[0] * kx, p[1] * ky, p[2] * kx, p[3] * ky) <= tol + w;
            } else if (it.k === 'rect' || it.k === 'blur') {
                if (p.length >= 4) {
                    var x0 = Math.min(p[0], p[2]) * kx, x1 = Math.max(p[0], p[2]) * kx;
                    var y0 = Math.min(p[1], p[3]) * ky, y1 = Math.max(p[1], p[3]) * ky;
                    if (it.k === 'blur') hit = px >= x0 - tol && px <= x1 + tol && py >= y0 - tol && py <= y1 + tol;
                    else hit = distSeg(px, py, x0, y0, x1, y0) <= tol + w || distSeg(px, py, x1, y0, x1, y1) <= tol + w ||
                        distSeg(px, py, x1, y1, x0, y1) <= tol + w || distSeg(px, py, x0, y1, x0, y0) <= tol + w;
                }
            } else if (it.k === 'oval') {
                if (p.length >= 4) {
                    var cx = (p[0] + p[2]) / 2 * kx, cy = (p[1] + p[3]) / 2 * ky;
                    var rx = Math.abs(p[2] - p[0]) / 2 * kx, ry = Math.abs(p[3] - p[1]) / 2 * ky;
                    if (rx > 1e-6 && ry > 1e-6) {
                        var q = Math.hypot((px - cx) / rx, (py - cy) / ry);
                        hit = Math.abs(q - 1) * Math.min(rx, ry) <= tol + w;
                    }
                }
            } else if (it.k === 'step') {
                hit = Math.hypot(p[0] * kx - px, p[1] * ky - py) <= tol + clamp((it.w || SIZES.m) * 3.2, 0.014, 0.06);
            } else if (it.k === 'text') {
                var fsz = clamp((it.w || SIZES.m) * 6.5, 0.018, 0.12);
                var lines = String(it.t || '').split('\n'), longest = 0;
                lines.forEach(function (l) { if (l.length > longest) longest = l.length; });
                var tw = longest * fsz * 0.6, th = lines.length * fsz * 1.18;
                hit = px >= p[0] * kx - tol && px <= p[0] * kx + tw + tol && py >= p[1] * ky - tol && py <= p[1] * ky + th + tol;
            }
            if (hit) return i;
        }
        return -1;
    }

    // ── фигура из штриха пера (задержал перо в конце — штрих выпрямился) ──
    // pts — точки пера в долях; aspect — ширина/высота картинки. Ответ —
    // {k:'line'|'rect'|'oval', p:[x0,y0,x1,y1]} или null (не похоже ни на что:
    // штрих остаётся, как нарисован). Пороги — по набору нарисованных рукой
    // штрихов из теста (tests/js/screen-annotate-core.test.mjs).
    function recognize(pts, aspect) {
        aspect = aspect || 16 / 9;
        var n = pts.length / 2;
        if (n < 3) return null;
        var kx = aspect >= 1 ? aspect : 1, ky = aspect >= 1 ? 1 : 1 / aspect;
        var xs = [], ys = [], L = 0;
        for (var i = 0; i < n; i++) { xs.push(pts[i * 2] * kx); ys.push(pts[i * 2 + 1] * ky); }
        for (var j = 1; j < n; j++) L += Math.hypot(xs[j] - xs[j - 1], ys[j] - ys[j - 1]);
        if (L < 0.03) return null;
        var D = Math.hypot(xs[n - 1] - xs[0], ys[n - 1] - ys[0]);
        // Прямая: путь почти равен расстоянию между концами, и ни одна точка
        // не уходит от хорды дальше 4 % её длины.
        if (D / L > 0.9) {
            var dev = 0;
            for (var a = 0; a < n; a++) dev = Math.max(dev, distSeg(xs[a], ys[a], xs[0], ys[0], xs[n - 1], ys[n - 1]));
            if (dev <= Math.max(0.006, D * 0.06)) return { k: 'line', p: [pts[0], pts[1], pts[pts.length - 2], pts[pts.length - 1]] };
            return null;
        }
        var minX = Math.min.apply(null, xs), maxX = Math.max.apply(null, xs);
        var minY = Math.min.apply(null, ys), maxY = Math.max.apply(null, ys);
        var bw = maxX - minX, bh = maxY - minY;
        if (bw < 0.02 || bh < 0.02) return null;
        // Замкнутая: концы сошлись (≤ 22 % большей стороны), и обход не
        // меньше 70 % периметра рамки.
        if (D > 0.22 * Math.max(bw, bh) || L < 0.7 * 2 * (bw + bh) * 0.785) return null;
        var cx = (minX + maxX) / 2, cy = (minY + maxY) / 2, rx = bw / 2, ry = bh / 2;
        var eO = 0, eR = 0;
        for (var b = 0; b < n; b++) {
            var x = xs[b], y = ys[b];
            eO += Math.abs(Math.hypot((x - cx) / rx, (y - cy) / ry) - 1) * Math.min(rx, ry);
            eR += Math.min(Math.abs(x - minX), Math.abs(x - maxX), Math.abs(y - minY), Math.abs(y - maxY));
        }
        eO /= n; eR /= n;
        var unit = Math.min(bw, bh);
        var box = [minX / kx, minY / ky, maxX / kx, maxY / ky];
        if (eR < eO && eR <= unit * 0.075) return { k: 'rect', p: box };
        if (eO <= unit * 0.09) return { k: 'oval', p: box };
        if (eR <= unit * 0.075) return { k: 'rect', p: box };
        return null;
    }

    // Shift при фигуре: квадрат / круг / угол, кратный 45°. Доли пересчитываются
    // через aspect, иначе «квадрат» на 16:9 вышел бы прямоугольником.
    function constrain(k, x0, y0, x1, y1, aspect) {
        aspect = aspect || 16 / 9;
        var dx = (x1 - x0) * aspect, dy = y1 - y0;
        if (k === 'line' || k === 'arrow') {
            var ang = Math.round(Math.atan2(dy, dx) / (Math.PI / 4)) * (Math.PI / 4);
            var len = Math.hypot(dx, dy);
            return [x0, y0, x0 + Math.cos(ang) * len / aspect, y0 + Math.sin(ang) * len];
        }
        var m = Math.max(Math.abs(dx), Math.abs(dy));
        return [x0, y0, x0 + (dx < 0 ? -m : m) / aspect, y0 + (dy < 0 ? -m : m)];
    }

    // ── сеть: операции над документом (у зрителя /screen-share) ──────────
    // Ведущий шлёт операции, зритель применяет их к своей копии. Время
    // завершения зритель ставит по СВОИМ часам: часы двух машин не сравнимы,
    // а угасание должно длиться столько же, сколько у ведущего.
    //   {o:'add', it}        — пометка целиком (или начало штриха)
    //   {o:'pts', id, p}     — ещё точки идущего штриха
    //   {o:'set', id, p, t}  — точки/текст заменены (фигура тянется, распознана)
    //   {o:'end', id}        — штрих завершён
    //   {o:'del', ids}       — убраны (ластик, отмена)
    //   {o:'clr'}            — всё убрано
    //   {o:'all', items}     — всё состояние (для подключившихся позже)
    //   {o:'ptr', x, y, k, c, r} / {o:'ptr'} — указатель есть / ушёл
    function cleanItem(it) {
        if (!it || typeof it.id !== 'string' || !isKind(it.k)) return null;
        var p = Array.isArray(it.p) ? it.p.slice(0, MAX_POINTS * 2).map(function (v) { return clamp(+v || 0, -0.05, 1.05); }) : [];
        var out = { id: it.id.slice(0, 40), k: it.k, c: /^#[0-9a-f]{3,8}$/i.test(it.c || '') ? it.c : '#ff2d55',
            w: clamp(+it.w || SIZES.m, 0.001, 0.05), p: p, life: clamp(+it.life || 0, 0, 600000), done: null };
        if (it.k === 'text') out.t = String(it.t || '').slice(0, 500);
        if (it.k === 'step') out.n = clamp(Math.round(+it.n || 1), 1, 999);
        return out;
    }
    function applyOp(st, op, now) {
        if (!op || typeof op.o !== 'string') return false;
        var items = st.items, i;
        function find(id) { for (var k = 0; k < items.length; k++) if (items[k].id === id) return k; return -1; }
        switch (op.o) {
            case 'add':
                var it = cleanItem(op.it);
                if (!it) return false;
                if (op.it.done) it.done = now;
                if (it.k === 'laser') st.laserIdleAt = now;
                i = find(it.id);
                if (i >= 0) items[i] = it; else items.push(it);
                if (items.length > MAX_ITEMS) items.splice(0, items.length - MAX_ITEMS);
                return true;
            case 'pts':
                i = find(op.id);
                if (i < 0 || !Array.isArray(op.p)) return false;
                if (items[i].p.length / 2 < MAX_POINTS) {
                    for (var q = 0; q < op.p.length; q++) items[i].p.push(clamp(+op.p[q] || 0, -0.05, 1.05));
                }
                if (items[i].k === 'laser') st.laserIdleAt = now;
                return true;
            case 'set':
                i = find(op.id);
                if (i < 0) return false;
                if (Array.isArray(op.p)) items[i].p = op.p.slice(0, MAX_POINTS * 2).map(function (v) { return clamp(+v || 0, -0.05, 1.05); });
                if (op.k && isKind(op.k)) items[i].k = op.k;
                if (typeof op.t === 'string') items[i].t = op.t.slice(0, 500);
                return true;
            case 'end':
                i = find(op.id);
                if (i < 0) return false;
                items[i].done = now;
                if (items[i].k === 'laser') st.laserIdleAt = now;
                return true;
            case 'del':
                var ids = Array.isArray(op.ids) ? op.ids : [];
                var hit = false;
                for (var d = items.length - 1; d >= 0; d--) if (ids.indexOf(items[d].id) >= 0) { items.splice(d, 1); hit = true; }
                return hit;
            case 'clr':
                items.length = 0;
                return true;
            case 'all':
                items.length = 0;
                (Array.isArray(op.items) ? op.items : []).forEach(function (x) {
                    var c = cleanItem(x);
                    if (c) { if (x.done) c.done = now; items.push(c); }
                });
                return true;
            case 'zoom':
                st.zoom = zoomTo(st.zoom || null, op.r || null, now);
                return true;
            case 'vptr':
                // указка зрителя (её разослал ведущий): по id, гаснет сама
                if (typeof op.id !== 'string') return false;
                st.remote = st.remote || {};
                if (typeof op.x === 'number' && typeof op.y === 'number') {
                    st.remote[op.id.slice(0, 60)] = { x: clamp(op.x, 0, 1), y: clamp(op.y, 0, 1),
                        c: /^#[0-9a-f]{3,8}$/i.test(op.c || '') ? op.c : '#0a84ff', n: String(op.n || '').slice(0, 40), t: now };
                } else delete st.remote[op.id];
                return true;
            case 'ptr':
                st.ptr = (typeof op.x === 'number' && typeof op.y === 'number')
                    ? { x: clamp(op.x, 0, 1), y: clamp(op.y, 0, 1), k: op.k === 'spot' ? 'spot' : 'laser', c: /^#[0-9a-f]{3,8}$/i.test(op.c || '') ? op.c : '#ff2d55', r: clamp(+op.r || 0.16, 0.05, 0.5) }
                    : null;
                return true;
        }
        return false;
    }
    // Живые указки зрителей списком для render(); ушедшие убираются.
    function remoteList(st, now) {
        var out = [];
        if (!st || !st.remote) return out;
        for (var k in st.remote) {
            if (!Object.prototype.hasOwnProperty.call(st.remote, k)) continue;
            if (now - st.remote[k].t > REMOTE_MS) { delete st.remote[k]; continue; }
            out.push(st.remote[k]);
        }
        return out;
    }
    // Пометка для отправки: без локального времени.
    function wireItem(it) {
        var o = { id: it.id, k: it.k, c: it.c, w: it.w, p: it.p.slice(), life: it.life || 0 };
        if (it.t) o.t = it.t;
        if (it.n) o.n = it.n;
        if (it.done) o.done = 1;
        return o;
    }

    return {
        FADE_MS: FADE_MS, LASER_IDLE_MS: LASER_IDLE_MS, LASER_FADE_MS: LASER_FADE_MS, REMOTE_MS: REMOTE_MS, ZOOM_MS: ZOOM_MS,
        zoomNow: zoomNow, zoomBusy: zoomBusy, zoomTo: zoomTo, zoomCompose: zoomCompose, drawZoomed: drawZoomed, cleanRect: cleanRect,
        SIZES: SIZES, KINDS: KINDS, MAX_ITEMS: MAX_ITEMS, MAX_POINTS: MAX_POINTS,
        alphaOf: alphaOf, prune: prune, render: render, renderGuides: renderGuides, remoteList: remoteList,
        fitRect: fitRect, toNorm: toNorm, hitTest: hitTest, recognize: recognize,
        constrain: constrain, applyOp: applyOp, cleanItem: cleanItem, wireItem: wireItem,
        isDark: isDark
    };
});
